import React, { useState } from "react";
import type { WalletStandardWallet } from "../../joinsplit-v2/solana";
import { BaseWalletDialog } from "../../base-app/components/BaseWalletDialog";
import { RecoveryPhraseInput } from "../../base-app/components/RecoveryPhraseBackup";
import { recoveryPhraseToSpendingKey } from "../../joinsplit-v2/recoveryPhrase";
import {
  hasActiveMasterRecoverySession,
  hasAnyStoredMasterRecoverySession,
} from "../../joinsplit-v2/masterRecoveryStorage";

interface SolanaWalletModalProps {
  wallets: WalletStandardWallet[];
  chainLabel: string;
  onSelect: (
    wallet: WalletStandardWallet,
    options: {
      existingWallet: boolean;
      recoveryPhrase?: string;
      legacyRecoveryPhrase?: boolean;
      linkMasterWallet?: boolean;
      createSeparateMasterWallet?: boolean;
    },
  ) => void;
  onClose: () => void;
}

export default function SolanaWalletModal({
  wallets,
  chainLabel,
  onSelect,
  onClose,
}: SolanaWalletModalProps) {
  const [restoring, setRestoring] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [error, setError] = useState("");
  const [legacyPhrase, setLegacyPhrase] = useState(false);
  const [linkExisting, setLinkExisting] = useState(false);
  const [createSeparate, setCreateSeparate] = useState(false);
  const hasSavedMaster = hasAnyStoredMasterRecoverySession();
  const select = (wallet: WalletStandardWallet) => {
    if (
      createSeparate &&
      !window.confirm(
        "Create a separate StealthOS private wallet? It will have a NEW recovery phrase and will NOT contain the funds or notes of your existing StealthOS wallet. Your saved wallet and encrypted backups will remain stored on this device. Continue?",
      )
    )
      return;
    if (restoring) {
      try {
        recoveryPhraseToSpendingKey(phrase);
      } catch {
        setError("Enter a valid 12- or 24-word StealthOS recovery phrase.");
        return;
      }
    }
    onSelect(wallet, {
      existingWallet: false,
      ...(restoring
        ? {
            recoveryPhrase: phrase,
            ...(legacyPhrase ? { legacyRecoveryPhrase: true } : {}),
          }
        : {}),
      ...(!restoring && linkExisting ? { linkMasterWallet: true } : {}),
      ...(!restoring && createSeparate
        ? { createSeparateMasterWallet: true }
        : {}),
    });
    setPhrase("");
  };
  return (
    <BaseWalletDialog open title="Connect a wallet" onClose={onClose}>
      <p className="wallet-review-context">
        Connect a Solana wallet on {chainLabel}. Save your StealthOS recovery
        phrase so you can restore your private wallet.
      </p>
      <button
        type="button"
        className="wallet-secondary"
        aria-pressed={restoring}
        onClick={() => {
          setRestoring(!restoring);
          setPhrase("");
          setError("");
          setLegacyPhrase(false);
          setLinkExisting(false);
          setCreateSeparate(false);
        }}
      >
        {restoring
          ? "Back to normal connection"
          : "Restore with a StealthOS recovery phrase"}
      </button>
      {!restoring &&
        !hasActiveMasterRecoverySession() &&
        hasAnyStoredMasterRecoverySession() && (
          <p className="wallet-review-context">
            A StealthOS wallet is saved on this device. Connect its original
            public wallet on its original network to unlock it, then link this
            wallet. You can also restore its recovery phrase or explicitly
            create an independent wallet without deleting the saved one.
          </p>
        )}
      {restoring && (
        <>
          <p className="wallet-review-context">
            Restore your StealthOS wallet on Base, DogeOS and Solana, then
            connect a public wallet to use the app.
          </p>
          <RecoveryPhraseInput
            value={phrase}
            onChange={(value) => {
              setPhrase(value);
              setError("");
            }}
            error={error}
          />
          <details style={{ marginTop: 12 }}>
            <summary>Older chain-specific wallet</summary>
            <label style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <input
                type="checkbox"
                checked={legacyPhrase}
                onChange={(event) => setLegacyPhrase(event.target.checked)}
              />
              This is an older chain-specific 12-word phrase.
            </label>
          </details>
        </>
      )}
      {!restoring && hasActiveMasterRecoverySession() && (
        <label
          className="wallet-review-context"
          style={{ display: "flex", gap: 8, marginTop: 16 }}
        >
          <input
            type="checkbox"
            checked={linkExisting}
            onChange={(event) => {
              setLinkExisting(event.target.checked);
              if (event.target.checked) setCreateSeparate(false);
            }}
          />
          Use my existing StealthOS wallet and recovery phrase.
        </label>
      )}
      {!restoring && hasSavedMaster && (
        <>
          <label
            className="wallet-review-context"
            style={{ display: "flex", gap: 8, marginTop: 16 }}
          >
            <input
              type="checkbox"
              checked={createSeparate}
              onChange={(event) => {
                setCreateSeparate(event.target.checked);
                if (event.target.checked) setLinkExisting(false);
              }}
            />
            Create a separate new StealthOS private wallet for this Phantom
            account
          </label>
          {createSeparate && (
            <p className="wallet-review-context" role="status">
              This creates a NEW private wallet with an empty balance and a new
              recovery phrase. Your existing StealthOS wallet and encrypted
              records stay saved. To access existing private funds, restore
              their StealthOS recovery phrase instead.
            </p>
          )}
        </>
      )}
      {wallets.length === 0 ? (
        <p className="wallet-review-context">
          No compatible wallet was detected. Install Phantom, Solflare or
          Backpack and reload.
        </p>
      ) : (
        wallets.map((wallet) => (
          <button
            key={wallet.name}
            type="button"
            className="wallet-account-option"
            onClick={() => select(wallet)}
          >
            {wallet.icon && (
              <img src={wallet.icon} alt="" width={28} height={28} />
            )}
            <span>{wallet.name}</span>
          </button>
        ))
      )}
    </BaseWalletDialog>
  );
}
